// shared-lib/labor/floorLevel.js
//
// The scan's FLOOR LEVEL (claude-docs/2026-10-21-standard-variations-design.md,
// 6b, D8). Raw `story` misreads two cases: a master bath scanned on its own is
// storey 0 though it is upstairs, and a scan that includes a basement numbers
// the basement 0, so the ground floor is 1 (`story-grouping.js` keeps only the
// order). The structure's `lowestFloor` — set by the user in the structure
// panel — says what storey 0 IS, and every other storey follows in order.
//
// Unset reads as ground, and so does any value that is not one of the three
// settings: that is exactly how `story` has always been read, so nothing
// moves until someone sets it. A room with no `story` is on the scan's lowest
// floor, as the structure panel already sorts it (`story ?? 0`). Pure.

const LOWEST_FLOOR_SETTINGS = Object.freeze(['basement', 'ground', 'upper']);

// how far storey 0 sits from the ground floor, by setting
const OFFSET = Object.freeze({ basement: -1, ground: 0, upper: 1 });

/** The structure's lowest-floor setting — 'ground' when unset or unknown. */
const lowestFloorOf = (structure) =>
  LOWEST_FLOOR_SETTINGS.includes(structure?.lowestFloor)
    ? structure.lowestFloor
    : 'ground';

/**
 * A storey's level above the ground floor (0 = ground, 1 = the floor above,
 * -1 = a basement) under the scan's lowest-floor setting.
 */
const floorLevelOf = (story, lowestFloor) =>
  (Number.isFinite(story) ? story : 0) + OFFSET[lowestFloorOf({ lowestFloor })];

/**
 * Is this place — `{ story, lowestFloor }`, the room's storey and the scan's
 * setting — on an upper floor (ground + 1 or above)? No place, no floor.
 */
const isUpperFloor = (place) =>
  Boolean(place) && floorLevelOf(place.story, place.lowestFloor) >= 1;

module.exports = {
  LOWEST_FLOOR_SETTINGS,
  lowestFloorOf,
  floorLevelOf,
  isUpperFloor,
};
